import {
  bumpPrestadorSessionGen,
  clearPrestadorAuthToken,
  PRESTADOR_CPF_KEY,
  PRESTADOR_DADOS_KEY,
  PRESTADOR_HASH_KEY,
  PRESTADOR_ID_KEY,
  PRESTADOR_TOKEN_KEY,
  setPrestadorAuthToken,
  tenantHashPrestador,
} from "@/services/ApiPrestador";
import AsyncStorage from "@/platform/async-storage";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Sessão do PRESTADOR — separada da sessão do colaborador (AuthContext).
 *
 * Dois contextos coexistem de propósito: o mesmo aparelho pode ter um CLT logado
 * e um prestador logado, e NADA pode se misturar (ids das duas tabelas colidem).
 * Chaves de storage, token e geração de sessão são exclusivos (ApiPrestador).
 *
 * Este contexto nasce mínimo (o que o login precisa) e cresce nas próximas
 * etapas — registro de diária, offline-first e troca de empresa.
 */

/**
 * Normaliza o modo de registro da diária vindo do backend.
 *
 * Só `'Q'` é QR Code; QUALQUER outra coisa (vazio, backend antigo, lixo de coluna SET —
 * o MySQL grava string vazia em SET inválido sem reclamar) cai em `'L'`. A lista sempre
 * funciona; a câmera depende de permissão e de um cartaz impresso no posto.
 */
export const normalizarModoRegistro = (v: unknown): "L" | "Q" =>
  String(v ?? "")
    .trim()
    .toUpperCase() === "Q"
    ? "Q"
    : "L";

export type PrestadorDados = {
  /** id_adm_colaborador_freelancer no tenant ATUAL (colide com ids de CLT!). */
  idPrestador: number;
  cpf: string;
  /** hash da empresa (tenant) da sessão. */
  hash: string;
  nome?: string;
  cargo?: string;
  empresaNome?: string;
  empresaLogo?: string;
  /** Foto do prestador (tab_adm_colaboradores_freelancers.foto). */
  foto?: string;
  /**
   * `ativo_inativo` do vínculo NESTE tenant ('A' | 'I').
   *
   * Fica na sessão (e não só em memória) porque o bloqueio do registro precisa
   * valer OFFLINE: o prestador abre o app sem rede e o botão já tem que refletir
   * o último status conhecido. Ausente = desconhecido → não bloqueia (fail-open).
   */
  ativo?: string;
  /**
   * Como o prestador escolhe o posto ao registrar a diária: `'L'` (lista) ou `'Q'` (lê o
   * QR Code do posto). Origem: `tab_adm_colaboradores_freelancers_config`, configuração
   * POR TENANT e versionada — trocar de empresa pode trocar o modo, por isso ela vem no
   * login E no trocarEmpresa, não só no `perfil/carregar`.
   *
   * Fica na sessão persistida pelo mesmo motivo do `ativo`: a tela de registro precisa
   * decidir qual caminho abrir OFFLINE, no primeiro segundo, sem esperar rede. Ausente ou
   * desconhecido = `'L'` — a lista sempre funciona, a câmera depende de permissão e de um
   * cartaz impresso.
   */
  modoRegistro?: string;
};

type PrestadorAuthContextValue = {
  token: string | null;
  prestador: PrestadorDados | null;
  isHydrated: boolean;
  /** Hash do tenant da sessão (claim c_hash do token) — fonte p/ escopo local. */
  tenantHash: string;
  signIn: (token: string, dados: PrestadorDados) => Promise<void>;
  signOut: () => Promise<void>;
  atualizarPrestador: (parcial: Partial<PrestadorDados>) => Promise<void>;
};

const PrestadorAuthContext = createContext<PrestadorAuthContextValue>({
  token: null,
  prestador: null,
  isHydrated: false,
  tenantHash: "",
  signIn: async () => {},
  signOut: async () => {},
  atualizarPrestador: async () => {},
});

export const PrestadorAuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [prestador, setPrestador] = useState<PrestadorDados | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [tenantHash, setTenantHash] = useState("");

  // Hidratação: restaura a sessão do prestador ao abrir o app.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        // Token SOZINHO e primeiro (mesma regra do colaborador, 05/10/2026): no Android um
        // valor acima de ~2 MB não volta da leitura do AsyncStorage, e os dados do prestador
        // levam a foto. Lidos no mesmo Promise.all, a falha dos dados levava o token junto.
        // Erro de leitura tenta de novo (3×): falha passageira de I/O não pode virar logout.
        let t: string | null = null;
        for (let i = 1; i <= 3; i++) {
          try {
            t = await AsyncStorage.getItem(PRESTADOR_TOKEN_KEY);
            break;
          } catch {
            if (i < 3) await new Promise((r) => setTimeout(r, 150));
          }
        }
        if (!vivo) return;
        if (t) {
          setPrestadorAuthToken(t);
          setToken(t);
          setTenantHash(await tenantHashPrestador().catch(() => ""));
        }
        const dadosRaw = await AsyncStorage.getItem(PRESTADOR_DADOS_KEY).catch(
          () => null,
        );
        if (!vivo) return;
        if (dadosRaw) {
          try {
            setPrestador(JSON.parse(dadosRaw));
          } catch {}
        }
      } finally {
        if (vivo) setIsHydrated(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const signIn = useCallback(
    async (novoToken: string, dados: PrestadorDados) => {
      // Sessão NOVA: invalida (síncrono) qualquer gravação assíncrona em voo da
      // sessão anterior — mesmo padrão do colaborador.
      bumpPrestadorSessionGen();
      setPrestadorAuthToken(novoToken);
      setToken(novoToken);
      setPrestador(dados);
      await Promise.all([
        AsyncStorage.setItem(PRESTADOR_TOKEN_KEY, novoToken),
        AsyncStorage.setItem(PRESTADOR_ID_KEY, String(dados.idPrestador ?? 0)),
        AsyncStorage.setItem(PRESTADOR_HASH_KEY, dados.hash ?? ""),
        AsyncStorage.setItem(PRESTADOR_CPF_KEY, dados.cpf ?? ""),
        AsyncStorage.setItem(PRESTADOR_DADOS_KEY, JSON.stringify(dados)),
      ]);
      setTenantHash(await tenantHashPrestador().catch(() => ""));
    },
    [],
  );

  const signOut = useCallback(async () => {
    bumpPrestadorSessionGen();
    clearPrestadorAuthToken();
    setToken(null);
    setPrestador(null);
    setTenantHash("");
    // Remove SÓ as chaves do prestador — a sessão do colaborador no mesmo
    // aparelho não pode ser afetada.
    await Promise.all(
      [
        PRESTADOR_TOKEN_KEY,
        PRESTADOR_ID_KEY,
        PRESTADOR_HASH_KEY,
        PRESTADOR_CPF_KEY,
        PRESTADOR_DADOS_KEY,
      ].map((k) => AsyncStorage.removeItem(k).catch(() => {})),
    );
  }, []);

  const atualizarPrestador = useCallback(
    async (parcial: Partial<PrestadorDados>) => {
      setPrestador((prev) => {
        const proximo = { ...(prev ?? ({} as PrestadorDados)), ...parcial };
        void AsyncStorage.setItem(
          PRESTADOR_DADOS_KEY,
          JSON.stringify(proximo),
        ).catch(() => {});
        return proximo;
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      token,
      prestador,
      isHydrated,
      tenantHash,
      signIn,
      signOut,
      atualizarPrestador,
    }),
    [
      token,
      prestador,
      isHydrated,
      tenantHash,
      signIn,
      signOut,
      atualizarPrestador,
    ],
  );

  return (
    <PrestadorAuthContext.Provider value={value}>
      {children}
    </PrestadorAuthContext.Provider>
  );
};

export const usePrestadorAuth = () => useContext(PrestadorAuthContext);

export default PrestadorAuthContext;
